import { useState } from 'react';
import { useClientTimeline } from '@offertify/query';
import type { AuditLogEntry } from '@offertify/core';
import { ACTION_COLORS, ACTION_LABELS, RESOURCE_LABELS } from '../../utils/constants';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  PlusCircle,
  Edit2,
  Send,
  CheckCircle,
  XCircle,
  Trash2,
  Loader2,
  Clock,
  ChevronDown,
} from 'lucide-react';

interface ClientTimelineTabProps {
  clientId: string;
}

const ACTION_ICONS: Record<string, React.ReactNode> = {
  create: <PlusCircle className="h-4 w-4" />,
  update: <Edit2 className="h-4 w-4" />,
  send: <Send className="h-4 w-4" />,
  accept: <CheckCircle className="h-4 w-4" />,
  reject: <XCircle className="h-4 w-4" />,
  delete: <Trash2 className="h-4 w-4" />,
};

const DOT_COLORS: Record<string, string> = {
  create: 'bg-green-500',
  update: 'bg-blue-500',
  send: 'bg-yellow-500',
  accept: 'bg-emerald-500',
  reject: 'bg-orange-500',
  delete: 'bg-red-500',
};

export default function ClientTimelineTab({ clientId }: ClientTimelineTabProps) {
  const [page, setPage] = useState(1);
  const limit = 20;

  // Accumulate all pages for infinite-scroll style
  const { data, isLoading, isFetching } = useClientTimeline(clientId, { page, limit });

  const [allEntries, setAllEntries] = useState<AuditLogEntry[]>([]);

  // Merge new data when it arrives
  const entries = (() => {
    const currentData = data?.data || [];
    if (page === 1) return currentData;
    // For subsequent pages, combine with accumulated entries
    const seen = new Set(allEntries.map((e) => e.id));
    const merged = [...allEntries];
    for (const entry of currentData) {
      if (!seen.has(entry.id)) merged.push(entry);
    }
    return merged;
  })();

  // Update accumulated entries when data changes
  if (data?.data && entries !== allEntries) {
    // Using a ref pattern would be cleaner, but for simplicity we allow the state update
    if (page > 1 && entries.length > allEntries.length) {
      setAllEntries(entries);
    } else if (page === 1 && data.data.length > 0 && allEntries.length === 0) {
      setAllEntries(data.data);
    }
  }

  const pagination = data?.pagination;
  const hasMore = pagination ? page < pagination.pages : false;

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">Nessuna attivita registrata</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-4">
          {entries.map((entry) => (
            <div key={entry.id} className="relative pl-10">
              {/* Dot */}
              <div
                className={`absolute left-2.5 top-1.5 w-3 h-3 rounded-full ring-2 ring-white ${
                  DOT_COLORS[entry.action] || 'bg-gray-400'
                }`}
              />

              {/* Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">
                      {ACTION_ICONS[entry.action] || <Edit2 className="h-4 w-4" />}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                    <span className="text-sm text-gray-700">
                      {RESOURCE_LABELS[entry.resource_type] || entry.resource_type}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDistanceToNow(new Date(entry.created_at), {
                      addSuffix: true,
                      locale: it,
                    })}
                  </span>
                </div>

                <div className="mt-1 text-xs text-gray-500">
                  {entry.user?.name || 'Utente sconosciuto'}
                </div>

                {/* Expandable details */}
                {(entry.old_values || entry.new_values) && (
                  <details className="mt-2">
                    <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                      Dettagli modifiche
                    </summary>
                    <div className="mt-2 bg-gray-50 rounded p-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                      {Object.keys({
                        ...entry.old_values,
                        ...entry.new_values,
                      }).map((key) => {
                        const oldVal = entry.old_values?.[key];
                        const newVal = entry.new_values?.[key];
                        if (oldVal === newVal) return null;
                        return (
                          <div key={key} className="border-b border-gray-200 pb-1 last:border-0">
                            <span className="font-medium text-gray-700">{key}: </span>
                            {oldVal !== undefined && (
                              <span className="text-red-600 line-through mr-1">
                                {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}
                              </span>
                            )}
                            {newVal !== undefined && (
                              <span className="text-green-600">
                                {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setPage(page + 1)}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Carica altro
          </button>
        </div>
      )}
    </div>
  );
}
